import React, { useState } from "react";
import { t } from "i18next";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import CampaignIcon from "@mui/icons-material/Campaign";

interface AdminMessageDialogProps {
  message: string | null;
  onDismiss: () => void;
  onReply: (text: string) => void;
}

export default function AdminMessageDialog({ message, onDismiss, onReply }: AdminMessageDialogProps) {
  const [replyText, setReplyText] = useState("");
  const [replySent, setReplySent] = useState(false);

  function handleReply() {
    if (!replyText.trim()) return;
    onReply(replyText.trim());
    setReplySent(true);
    setReplyText("");
  }

  function handleDismiss() {
    setReplySent(false);
    setReplyText("");
    onDismiss();
  }

  return (
    <Dialog open={message !== null} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CampaignIcon color="info" />
        {t("admin_message_title")}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ my: 1, whiteSpace: "pre-wrap" }}>
          {message}
        </Typography>
        {!replySent ? (
          <Box display="flex" gap={1} mt={3}>
            <TextField
              size="small"
              fullWidth
              placeholder={t("admin_message_reply_placeholder")}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleReply()}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleReply}
              disabled={!replyText.trim()}
              sx={{ flexShrink: 0 }}
            >
              {t("send")}
            </Button>
          </Box>
        ) : (
          <Typography variant="body2" color="success.main" align="center" sx={{ mt: 2 }}>
            {t("admin_message_reply_sent")}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDismiss}>{t("dismiss")}</Button>
      </DialogActions>
    </Dialog>
  );
}
